<template>
  <div
    v-if="siteIsLoadedAndEnabled"
    id="app"
    :style="`font-family: '${font}', Helvetica, Arial, sans-serif`"
  >
    <a
      ref="skipToContent"
      href="#container"
      class="skip-to-content"
    >
      {{ componentTranslations.globals.skip_to_content }}
    </a>
    <component
      :is="newSearchEnabled ? 'AdvancedSearch' : 'OldSearch'"
      v-if="isSearchOpen"
      :translations="componentTranslations.globals"
    />
    <Lightbox
      v-if="showLightbox"
      :translations="componentTranslations.globals"
    />
    <div
      v-if="hasGlobalContainers"
      class="global-containers"
    >
      <ViewContainer
        v-for="container in globalContainers"
        :key="container.containerIndex"
        :container="container"
        :container-idx="container.containerIndex"
      />
    </div>
    <header id="view-header">
      <AlertContainer
        v-if="isClient"
        :hide-alerts="hideAlerts"
        @keyboard-close="focusTopElement"
      />
      <div
        v-if="isIntranet && $auth?.value?.loggedIn"
        class="intranet-header"
      >
        <div>
          <h2>{{ $t('intranet.navHeading', { orgName: primaryOrgName }) }}</h2>
          <div class="auth-info">
            <span
              >{{ $t('intranet.userHeading') }}<strong>&nbsp;{{ $auth?.value?.user?.profile?.name }}</strong></span
            >
            <TSAvatar
              size="small"
              height="30px"
              :src="$auth?.value?.user?.profile?.avatar"
            />
            <a
              class="logout"
              href="#"
              @click="logout"
              >{{ $t('intranet.logout') }}</a
            >
          </div>
        </div>
      </div>
      <cms-header
        ref="cms-header"
        :theme="headerOptions"
        :content="content"
        :current-domain="domain"
        :is-homepage="isHomepage"
        :media-subdomain="mediaSubdomain"
        @language-change="languageChange"
        @toggle-mobile-menu="toggleMobileMenu"
        @toggle-menu="toggleMenu"
        @toggle-search="toggleSearch"
      />
      <cms-menu
        v-if="!mobileHeaderStatus"
        :theme="menuOptions"
        :content="content"
        :is-chat-drawer-open="isChatDrawerOpen"
        :is-chatbot-clicked="chatbotClicked"
        @close-menu="closeAndFocusMenuButton"
      />
    </header>
    <div
      id="container"
      class="main"
      role="main"
    >
      <slot />
    </div>
    <ViewFooter />
    <StickyFooter v-if="displayStickyFooter" />
    <AiChatWidget
      v-if="streamApiKey"
      @chatbot-click="handleChatbotClick"
    />
    <browser-warning
      v-if="showingBrowserNotice"
      @close="closeBrowserModal()"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, useStore } from 'vuex';
import * as cheerio from 'cheerio';
import get from 'lodash/get';
import kebabCase from 'lodash/kebabCase';
import isEmpty from 'lodash/isEmpty';

import { formatPageTitle } from '../helpers/strings';
import ViewFooter from '../components/home_page/footers/ViewFooter.vue';
import Lightbox from '../components/global/Lightbox.vue';
import OldSearch from '../components/search/Search.vue';
import AdvancedSearch from '../components/improved_search/AdvancedSearch.vue';
import AlertContainer from '../components/home_page/alert_banner/AlertContainer.vue';
import BrowserWarning from '../components/global/BrowserWarning.vue';
import StickyFooter from '../components/home_page/sticky_footer/ViewStickyFooter.vue';
import ViewContainer from '../components/home_page/ViewContainer.vue';
import TSAvatar from '../components/intranet/dscl/Avatar.vue';
import AiChatWidget from '../components/global/AiChatWidget.vue';
import { getContrastColor } from '../helpers/colors';
import { useDialogObserver } from '@/composables/useDialogObserver';
import { useAiChatWidget } from '@/composables/useAiChatWidget';
// import { useReCaptcha } from 'vue-recaptcha-v3';

export default {
  name: 'App',
  components: {
    BrowserWarning,
    Lightbox,
    OldSearch,
    AdvancedSearch,
    AlertContainer,
    StickyFooter,
    ViewFooter,
    ViewContainer,
    TSAvatar,
    AiChatWidget,
  },
  setup() {
    const { $ld } = useNuxtApp();
    const newSearchEnabled = computed(() => Boolean($ld?.flags?.cmsAdvancedSearch));
    const { isSearchOpen, toggleSearch } = useSearch(newSearchEnabled);
    const streamApiKey = useRuntimeConfig().public.streamApiKey;

    // const { executeRecaptcha, recaptchaLoaded, instance } = useReCaptcha();

    // onBeforeMount(() => {
    //   instance.value.showBadge();
    // });
    // onBeforeUnmount(() => {
    //   instance.value.hideBadge();
    // });
    // const recaptcha = async () => {
    //   // (optional) Wait until recaptcha has been loaded.
    //   await recaptchaLoaded();

    // // Execute reCAPTCHA with action "login".
    // const token = await executeRecaptcha();

    // // Do stuff with the received token.
    // return token;
    // };
    const store = useStore();
    let script = [];
    const styleTags = [];
    const linkTags = store.state.globals.favicon?.url
      ? [{ rel: 'icon', type: 'image/x-icon', href: store.state.globals.favicon.url }]
      : [];
    const organizationClassName = kebabCase(store.state.globals.info.school_name);

    linkTags.push(
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: true },
      {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;700;900&family=Work+Sans:ital,wght@0,300;0,400;0,500;0,700;0,900;1,300;1,400;1,700&family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,900&family=Roboto+Slab:wght@300;400;500;700;900&display=swap',
      },
      { rel: 'stylesheet', href: '/alias_font_faces.css' },
    );

    let noscript = [];
    if (store.state.globals.settings?.embedCodes) {
      const {
        googleAnalytics,
        facebookPixel,
        googleTagManager: { head: googleTagManagerHead, body: googleTagManagerBody },
        customCodes,
      } = store.state.globals.settings.embedCodes;
      const scriptsArray = [
        { type: 'googleAnalytics', code: googleAnalytics },
        { type: 'facebookPixel', code: facebookPixel },
        { type: 'googleTagManagerHead', code: googleTagManagerHead },
        { type: 'googleTagManagerBody', code: googleTagManagerBody },
        ...customCodes.map((code) => ({ type: 'customCode', code })),
      ];
      const scriptTypesInsertedInFooter = ['googleTagManagerBody', 'customCode'];
      const scriptAttrsToMaintain = ['src', 'async', 'defer'];

      // Client Added Style Tags
      try {
        styleTags.push(
          ...scriptsArray
            .filter((scriptObj) => scriptObj.code)
            .flatMap((scriptObj) => {
              try {
                const $ = cheerio.load(scriptObj.code);
                return $('style')
                  .toArray()
                  .map((node) => ({
                    type: 'text/css',
                    innerHTML: $(node).html(),
                  }));
              } catch (error) {
                console.error('Error parsing style tags:', error);
                return [];
              }
            }),
        );
      } catch (error) {
        console.error('Error processing style tags:', error);
      }

      // Client Added Script Tags
      script = scriptsArray
        .filter((scriptObj) => scriptObj.code)
        .reduce((array, scriptObj) => {
          try {
            const $ = cheerio.load(scriptObj.code);
            const scripts = $('script')
              .toArray()
              .map((scriptNode) => {
                const $scriptNode = $(scriptNode);
                const currentAttrs = $scriptNode.attr();
                const validAttrs = Object.keys(currentAttrs).reduce((acc, key) => {
                  if (scriptAttrsToMaintain.includes(key) || key.startsWith('data-')) {
                    acc[key] = currentAttrs[key] === '' && !key.startsWith('data-') ? key : currentAttrs[key];
                  }
                  return acc;
                }, {});

                return {
                  ...validAttrs,
                  type: 'text/javascript',
                  body: scriptTypesInsertedInFooter.includes(scriptObj.id),
                  innerHTML: $scriptNode.html(),
                };
              });
            return [...array, ...scripts];
          } catch (error) {
            console.error('Error parsing script tags:', error);
            return array;
          }
        }, []);

      // Client Added Noscript Tags
      noscript = scriptsArray
        .filter((scriptObj) => scriptObj.code)
        .reduce((array, scriptObj) => {
          try {
            const $ = cheerio.load(scriptObj.code);
            const noScripts = $('noscript')
              .toArray()
              .map((node) => ({ innerHTML: $(node).html(), body: true }));
            return [...array, ...noScripts];
          } catch (error) {
            console.error('Error parsing noscript tags:', error);
            return array;
          }
        }, []);
    }
    if (store.state.theme?.template_code) {
      const externalScripts = store.state.theme.template_code.script_codes
        .filter((s) => s)
        .map((scriptUrl) => ({
          type: 'text/javascript',
          body: true,
          src: scriptUrl,
        }));
      const externalStylesheets = store.state.theme.template_code.style_codes
        .filter((s) => s)
        .map((styleUrl) => ({
          rel: 'stylesheet',
          href: styleUrl,
        }));

      script.push(...externalScripts);
      linkTags.push(...externalStylesheets);

      if (store.state.theme.template_code.css_code) {
        styleTags.push({
          type: 'text/css',
          innerHTML: store.state.theme.template_code.css_code,
        });
      }
      if (store.state.theme.template_code.js_code) {
        script.push({
          type: 'text/javascript',
          body: true,
          defer: true,
          innerHTML: store.state.theme.template_code.js_code,
        });
      }
    }

    const globalTheme = () => {
      const fallbackColor = '#000';
      const primaryColor = get(store.state.globals, 'info.primary_color.hex', fallbackColor);
      const secondaryColor = get(store.state.globals, 'info.secondary_color.hex', fallbackColor);
      return [
        `--primary-color: ${primaryColor};`,
        ` --primary-color-contrast: ${getContrastColor(primaryColor)};`,
        `--secondary-color: ${secondaryColor};`,
        `--secondary-color-contrast: ${getContrastColor(secondaryColor)};`,
        `--link-color: ${get(store.state.globals, 'info.links_color.hex', fallbackColor)};`,
        `--links-color: ${get(store.state.globals, 'info.links_color.hex', fallbackColor)};`,
        `--heading: ${get(store.state.globals, 'info.cms_heading_color.hex', fallbackColor)};`,
        `--heading-color: ${get(store.state.globals, 'info.cms_heading_color.hex', fallbackColor)};`,
        `--page-builder-color: ${get(store.state.globals, 'info.page_builder_theme_color.hex', fallbackColor)};`,
        `--alert-text-color: ${get(store.state.globals, 'info.alert_text_color.hex', fallbackColor)};`,
        `--alert-background-color: ${get(store.state.globals, 'info.alert_bg_color.hex', fallbackColor)};`,
        `--alert-link-color: ${get(store.state.globals, 'info.alert_link_color.hex', fallbackColor)};`,
        `--alert-lightbox-shadow-color: ${get(store.state.globals, 'info.alert_lightbox_overlay.hex', fallbackColor)};`,
        `--font-family: '${store.state.font}', Helvetica, Arial, sans-serif`,
      ];
    };

    script.unshift({
      type: 'text/javascript',
      body: false,
      innerHTML: `window.clientWorkStateTemp = JSON.parse(${JSON.stringify(
        JSON.stringify(store.state),
      )}); window.clientWorkState = JSON.parse(${JSON.stringify(JSON.stringify(store.getters.getClientWorkState))})`,
    });

    const robotsIndexMeta = store.state.domain.includes('sites.thrillshare')
      ? { hid: 'robots', name: 'robots', content: 'noindex' }
      : {};
    useHead({
      htmlAttrs: {
        lang: store.state.translation.locale,
      },
      titleTemplate: (titleChunk) => {
        if (!titleChunk) {
          return store.state.globals.info.school_name;
        }
        return formatPageTitle(store.state.globals, 'info.school_name', titleChunk);
      },
      meta: [
        // hid is used as unique identifier. Do not use `vmid` for it as it will not work
        { hid: 'description', name: 'description', content: store.state.globals.info.school_tag_line },
        { hid: 'og:type', property: 'og:type', content: 'website' },
        { hid: 'og:title', property: 'og:title', content: store.state.globals.info.school_name },
        {
          hid: 'og:description',
          property: 'og:description',
          content: store.state.globals.info.school_tag_line,
          tagPriority: 'critical',
        },
        { hid: 'og:image', property: 'og:image', content: store.state.globals.logo?.url, tagPriority: 'critical' },
        // Twitter Tags
        { hid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
        { hid: 'twitter:title', name: 'twitter:title', content: store.state.globals.info.school_name },
        { hid: 'twitter:description', name: 'twitter:description', content: store.state.globals.info.school_tag_line },
        {
          hid: 'twitter:image',
          name: 'twitter:image',
          content: store.state.globals.logo?.url,
          tagPriority: 'critical',
        },
        robotsIndexMeta,
      ],
      link: linkTags,
      style: styleTags,
      bodyAttrs: {
        class: computed(() =>
          store.state.showLightbox || store.state.menuStatus
            ? `${organizationClassName} noscroll`
            : organizationClassName,
        ),
        style: globalTheme,
      },
      script,
      noscript,
    });

    const { isIntranet } = useIntranet();

    if (isIntranet) {
      // this triggers a nuxt warning in console, we should figure out where to put this.
      // this should only exist at a page level, not a layout level
      definePageMeta({
        middleware: ['auth', 'intranet-roles'],
      });
    }

    useDialogObserver();

    const { isOpen: isChatDrawerOpen } = useAiChatWidget();

    return {
      isIntranet,
      newSearchEnabled,
      isSearchOpen,
      streamApiKey,
      toggleSearch,
      isChatDrawerOpen,
    };
  },
  data() {
    return {
      showingBrowserNotice: false,
      mobileHeaderStatus: false,
      isClient: false,
      chatbotClicked: false,
    };
  },
  computed: {
    ...mapState([
      'globals',
      'homepage',
      'theme',
      'font',
      'domain',
      'organizations',
      'menuStatus',
      'pathPrefix',
      'isThemePreview',
      'showLightbox',
      'menu',
      'customMenuItems',
      'configLoading',
      'featureFlags',
      'mediaSubdomain',
    ]),
    ...mapState('translation', ['language', 'languages', 'locale', 'componentTranslations']),
    ...mapGetters('translation', ['getComponentTranslation']),
    primaryOrgName() {
      return this.$store.state.organizations.find((org) => org.primary).name;
    },
    content() {
      return {
        customMenuItems: this.customMenuItems,
        globals: this.globals,
        hasCustomSchoolsMenu: this.hasCustomSchoolsDropdown,
        hasGalleryImages: this.hasGalleryImages,
        iconLinks: this.iconLinks,
        isEditing: false,
        isIFrame: false,
        isPreview: this.isThemePreview,
        isSearchOpen: this.isSearchOpen,
        langCode: this.locale,
        langText: this.language,
        languages: this.languages,
        menuOptions: this.menuOptions,
        newMobileHeader: this.newMobileHeader,
        orgDropdownTitle: this.orgDropdownTitle,
        orgMenuContent: this.orgMenuContent,
        organizations: this.organizations,
        pathPrefix: this.pathPrefix,
        sitemapMenuItems: this.menu,
        showMenu: this.menuStatus,
        translations: this.translations,
        utilities: this.utilities,
      };
    },
    languageOptions() {
      return ['en', 'es', 'ar', 'ru', 'fr', 'pt', 'zh', 'ko', 'sv', 'vi', 'sr'];
    },
    filteredLanguages() {
      // Product supported languages: Spanish, Chinese, Arabic, Portuguese, Korean, Russian, Swedish, Vietnamese, French, Serbian
      return this.languages.filter((language) => this.languageOptions.includes(language.code));
    },
    hasGalleryImages() {
      return this.homepage?.gallery?.slides.length > 0;
    },
    headerOptions() {
      return { ...this.theme.header.options, translations: { ...this.getHeaderValues } };
    },
    menuOptions() {
      return {
        ...this.theme.menu.options,
        translations: this.getComponentTranslation('menu'),
      };
    },
    siteIsLoadedAndEnabled() {
      const isDisabledOnCMSPublic = this.featureFlags.disable_cms_public;
      return !this.configLoading && (!isDisabledOnCMSPublic || this.isThemePreview);
    },
    utilities() {
      return this.homepage?.header?.links || [];
    },
    iconLinks() {
      return this.homepage?.header?.icon_links || [];
    },
    translations() {
      return { ...this.componentTranslations.globals, ...this.theme.translations };
    },
    getHeaderValues() {
      const headerValues = { ...this.getComponentTranslation('header') };
      if (this.isThemePreview) {
        headerValues.menu_dropdown_title = this.theme.header.options.menu_field;
        headerValues.schools_dropdown_title = this.theme.header.options.schools_field;
        headerValues.services_dropdown_title = this.theme.header.options.services_field;
      }
      return headerValues;
    },
    orgDropdownTitle() {
      return this.globals.info.organization_type === 'Municipality'
        ? this.getHeaderValues.services_dropdown_title || 'Services'
        : this.getHeaderValues.schools_dropdown_title;
    },
    hasCustomSchoolsDropdown() {
      return this.theme?.header?.options?.custom_schools_dropdown;
    },
    newMobileHeader() {
      if (this.theme?.header?.options?.version === 'H3') return true;
      return !!this.$store.state.featureFlags.cms_new_mobile_header_enabled;
    },
    orgMenuContent() {
      return this.homepage?.schools_menu?.menu || [];
    },
    displayStickyFooter() {
      const hasActiveStickyFooter = this.theme.sticky_footer?.options?.active && !isEmpty(this.homepage.sticky_footer);
      const currentPageHasStickyFooter = this.isHomepage || !this.homepage.sticky_footer?.show_only_on_homepage;
      return hasActiveStickyFooter && currentPageHasStickyFooter;
    },
    globalContainers() {
      const containersWithIndex = this.theme.containers.map((container, idx) => ({
        ...container,
        containerIndex: idx,
      }));
      return containersWithIndex.filter((container) => !!container.global);
    },
    hasGlobalContainers() {
      return !isEmpty(this.globalContainers);
    },
    hideAlerts() {
      return {
        banner: this.mobileHeaderStatus && this.menuStatus,
        modal: this.showingBrowserNotice,
      };
    },
    isHomepage() {
      return ['index', 'alias-index', 'alias-preview-token-id', 'preview-token-id'].includes(this.$route.name);
    },
  },
  async mounted() {
    this.isClient = true;
    let isIE = false;
    const ie = window.navigator.userAgent.indexOf('MSIE ');
    const browserNotice = window.sessionStorage.getItem('UNSUPPORTED_BROWSER_NOTICE');

    if (ie > 0 || !!navigator.userAgent.match(/Trident.*rv:11\./)) {
      isIE = true;
    }

    if (isIE && !browserNotice) {
      this.showingBrowserNotice = true;
    }
    // const label = document.createElement('label');
    // label.textContent = 'Google Recaptcha';
    // label.setAttribute('for', 'g-recaptcha-response');
    // label.classList.add('sr-only');
    // document.body.appendChild(label);

    this.fireLoadEvents();
  },
  methods: {
    async logout() {
      try {
        this.$store.commit('setNeedsRefresh', true);
        const oidcClient = useOidcClient()();
        await oidcClient.removeUser();
        const authCookie = useCookie('auth-token');
        authCookie.value = undefined;
        navigateTo('/intranet/login');
      } catch {
        console.warn('error logging out');
      }
    },
    toggleMenu(status) {
      this.$store.commit('toggleMenu', status);
    },
    async languageChange(lang) {
      const language = this.languages.find(({ code }) => code === lang.code);
      if (language.code !== this.locale) {
        await this.$store.dispatch('translation/setLangCookies', { ...language, shouldReload: true });
      }
    },
    focusMenuButton() {
      this.$nextTick(() => {
        const menubutton = document.getElementById('menubutton');
        if (!menubutton) return;
        menubutton.focus();
      });
    },
    closeAndFocusMenuButton() {
      this.$store.commit('closeMenu');
      this.focusMenuButton();
    },
    focusTopElement() {
      this.$nextTick(() => {
        this.$refs.skipToContent.focus();
      });
    },
    closeBrowserModal() {
      this.showingBrowserNotice = false;
      window.sessionStorage.setItem('UNSUPPORTED_BROWSER_NOTICE', true);
    },
    toggleMobileMenu(status) {
      this.toggleMenu(status);
      this.mobileHeaderStatus = status;
    },
    fireLoadEvents() {
      // see https://apptegy.atlassian.net/browse/CMS2-461
      if (this.isIntranet) return;

      const isDomReady = () => document.readyState === 'complete';
      const interval = setInterval(() => {
        if (isDomReady()) {
          clearInterval(interval); // Stop checking once the document is ready
          const event = new CustomEvent('appLoaded');
          window.dispatchEvent(event);
          document.dispatchEvent(event);
        }
      }, 50);
    },
    handleChatbotClick() {
      this.chatbotClicked = true;
      // Reset after a short delay to allow the focus trap to react
      this.$nextTick(() => {
        setTimeout(() => {
          this.chatbotClicked = false;
        }, 100);
      });
    },
    async handleFormSubmit(formData) {
      const recaptchaToken = '';
      try {
        // recaptchaToken = await this.recaptcha();
        // eslint-disable-next-line no-unused-vars
        const submitData = {
          name: formData.name,
          email: formData.email,
          description: formData.description,
          school: formData.school,
          topic: formData.topic,
          recaptcha_response: recaptchaToken,
          attachments: formData.attachments,
        };
        // TBD when backend is ready
        // this.$store.dispatch('search/submitContactForm', {
        //   submitData,
        //   writableApi: useRuntimeConfig().public.WRITABLE_BASE_API_URL,
        // });
      } catch (e) {
        console.error(e);
        return;
      }
    },
  },
};
</script>

<style lang="scss">
@use '../components/global/scss/mixins/breakpoints';
html,
body,
#app {
  margin: 0;
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  font-weight: 400;
  @media (prefers-reduced-motion: no-preference) {
    scroll-behavior: smooth;
  }
  &.no-scroll {
    height: 100%;
    overflow: hidden;
  }
}
h1 {
  font-weight: 700;
  &.bold {
    font-weight: 900;
  }
}
h2,
h3,
h4,
h5 {
  font-weight: 700;
}
h1,
h2,
h3,
h4,
h5,
h6,
p {
  margin: 0;
}

/*Text align for News articles*/
.text-center {
  text-align: center;
}
.text-left {
  text-align: left;
}
.text-right {
  text-align: right;
}
.text-justify {
  text-align: justify;
}
// ensure main container is higher than footer
.main#container {
  position: relative;
  z-index: 1;
}

button.button-action {
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  padding: 0;
  .button-action-content {
    display: block;
  }
  &,
  > .button-action-content {
    outline: none;
    text-align: left;
    white-space: nowrap;
  }
  &:focus-visible > .button-action-content {
    outline: 1px dotted #212121;
    outline: 5px auto -webkit-focus-ring-color;
  }
}
.noscroll {
  overflow: hidden;
}
.page-container {
  width: 90%;
  margin: 0 auto;
  text-align: left;
  padding-top: 30px;
  padding-bottom: 70px;
  &:last-child {
    margin-bottom: 80px;
  }
  .filter {
    width: auto;
    float: right;
    select {
      line-height: 22px;
      margin-top: 10px;
      margin-bottom: 20px;
      width: 250px;
      padding: 5px 8px;
      padding-right: 33px;
      border: none;
      appearance: none;
      background: url('../assets/small_arrow_down.png') no-repeat 100% 50%;
      font-size: 16px;
      text-transform: uppercase;
      color: #717171;
    }
  }
  .page-container-item {
    border-bottom: 1px solid #ddd;
    padding: 18px 0;
    margin: 0 auto;
    overflow: hidden;
    clear: both;
  }
}
.search-enter-from,
.search-leave-to {
  transform: scale(0.9);
  opacity: 0;
}
.search-enter-to,
.search-leave-from {
  transform: scale(1);
  opacity: 1;
}
th {
  font-weight: 700;
}
blockquote,
strong {
  font-weight: 700;
}
.skip-to-content {
  clip: rect(1px, 1px, 1px, 1px);
  clip-path: inset(50%);
  height: 1px;
  width: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  &:focus {
    width: auto;
    height: auto;
    background-color: #f1f1f1;
    border-radius: 3px;
    clip: auto;
    clip-path: none;
    color: #21759b;
    font-size: 14px;
    font-weight: bold;
    left: 5px;
    padding: 15px 23px 14px;
    top: 5px;
    text-align: center;
    z-index: 20;
  }
}

// Allows Google recaptcha privacy info to appear on top of modal
.grecaptcha-badge {
  z-index: 100;
}

.intranet-header {
  --big-screen-padding: 30px;
  --little-screen-padding: 10px;
  --intranet-nav-height-big: 60px;
  --intranet-nav-height-small: 45px;

  height: var(--intranet-nav-height-big);
  > div {
    color: white;
    position: fixed;
    z-index: 4;
    width: 100%;
    height: var(--intranet-nav-height-big);
    background-color: black;
    display: flex;
    align-items: center;
    justify-content: space-between;
    h2 {
      font-size: 16px;
      padding-left: var(--big-screen-padding);
    }
    .auth-info {
      display: flex;
      align-items: center;
      gap: 6px;
      padding-right: var(--big-screen-padding);
      .logout {
        color: #02a0e3;
      }
    }
  }
  @include breakpoints.screen('<=sm') {
    height: var(--intranet-nav-height-small);
    div {
      height: var(--intranet-nav-height-small);
      h2 {
        display: none;
      }
      .auth-info {
        padding-left: var(--little-screen-padding);
      }
    }
  }
}
</style>
